import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { getJson } from './api';

/**
 * Analytics → Accounting → Facilitators: what each facilitator and assistant
 * billed, the average flight, hotel, meals and other travel, and the lines
 * worth a look. Mirrors `apps/api/src/api/v3/profitability/facilitator-spend*.ts`.
 * Behind view_accounting: this is personal compensation.
 */

export const FACILITATORS_PATH = {
  list: '/analytics/accounting/facilitators',
  person: '/analytics/accounting/facilitators/:personId',
} as const;

export function facilitatorPersonPath(contactId: string): string {
  return FACILITATORS_PATH.person.replace(':personId', encodeURIComponent(contactId));
}

/** The window starts in 2023 unless the URL says otherwise (statements are near universal from 2023). */
export const SPEND_DEFAULT_FROM = '2023-01-01';

export const SPEND_CATEGORY = {
  fee: 'fee',
  assistant: 'assistant',
  airfare: 'airfare',
  hotel: 'hotel',
  carRental: 'carRental',
  transportation: 'transportation',
  gasTolls: 'gasTolls',
  meals: 'meals',
  mileage: 'mileage',
  parking: 'parking',
  miscTravel: 'miscTravel',
  other: 'other',
} as const;
export type SpendCategory = (typeof SPEND_CATEGORY)[keyof typeof SPEND_CATEGORY];

/** Column and filter labels, in display order. */
export const SPEND_CATEGORY_LABELS: Readonly<Record<SpendCategory, string>> = {
  [SPEND_CATEGORY.fee]: 'Facilitation fee',
  [SPEND_CATEGORY.assistant]: 'Assistants',
  [SPEND_CATEGORY.airfare]: 'Airfare',
  [SPEND_CATEGORY.hotel]: 'Hotel',
  [SPEND_CATEGORY.carRental]: 'Rental car',
  [SPEND_CATEGORY.transportation]: 'Transportation',
  [SPEND_CATEGORY.gasTolls]: 'Gas and tolls',
  [SPEND_CATEGORY.meals]: 'Meals',
  [SPEND_CATEGORY.mileage]: 'Mileage',
  [SPEND_CATEGORY.parking]: 'Parking',
  [SPEND_CATEGORY.miscTravel]: 'Misc. travel',
  [SPEND_CATEGORY.other]: 'Other',
};
export const SPEND_CATEGORY_ORDER: readonly SpendCategory[] = Object.values(SPEND_CATEGORY);

/** The travel categories, in the order the columns and averages read. */
export const TRAVEL_CATEGORIES = [
  SPEND_CATEGORY.airfare,
  SPEND_CATEGORY.hotel,
  SPEND_CATEGORY.carRental,
  SPEND_CATEGORY.transportation,
  SPEND_CATEGORY.gasTolls,
  SPEND_CATEGORY.meals,
  SPEND_CATEGORY.mileage,
  SPEND_CATEGORY.parking,
  SPEND_CATEGORY.miscTravel,
] as const satisfies readonly SpendCategory[];
export type TravelCategory = (typeof TRAVEL_CATEGORIES)[number];

/** Builds a record with one entry per travel category. */
export function perTravelCategory<T>(value: (category: TravelCategory) => T): Record<TravelCategory, T> {
  return {
    airfare: value(SPEND_CATEGORY.airfare),
    hotel: value(SPEND_CATEGORY.hotel),
    carRental: value(SPEND_CATEGORY.carRental),
    transportation: value(SPEND_CATEGORY.transportation),
    gasTolls: value(SPEND_CATEGORY.gasTolls),
    meals: value(SPEND_CATEGORY.meals),
    mileage: value(SPEND_CATEGORY.mileage),
    parking: value(SPEND_CATEGORY.parking),
    miscTravel: value(SPEND_CATEGORY.miscTravel),
  };
}

export const ROLE_FILTER = {
  all: 'all',
  facilitator: 'facilitator',
  assistant: 'assistant',
} as const;
export type RoleFilter = (typeof ROLE_FILTER)[keyof typeof ROLE_FILTER];
export type PersonRole = 'facilitator' | 'assistant' | 'other';

export const PERSON_ROLE_LABELS: Readonly<Record<PersonRole, string>> = {
  facilitator: 'Facilitator',
  assistant: 'Assistant',
  other: 'Other',
};

export type UnitBasis = 'event' | 'trip' | 'night' | 'stay' | 'day' | 'assistant' | 'line';
export type AverageBasis = UnitBasis | 'mixed';

/** "per …" for an average, in the reader's words. */
export const BASIS_LABELS: Readonly<Record<AverageBasis, string>> = {
  event: 'per event',
  trip: 'per trip',
  night: 'per night',
  stay: 'per stay',
  day: 'per day',
  assistant: 'per assistant',
  line: 'per line',
  mixed: 'per night or stay',
};

// ─── Wire types ──────────────────────────────────────────────────────────────

export interface SpendWindow {
  readonly from: string;
  readonly to: string;
}

export interface LinePerson {
  readonly contactId: string;
  readonly name: string;
  readonly email: string;
  readonly userId: string | null;
}

export interface SpendTotals {
  readonly total: number;
  readonly fee: number;
  readonly assistant: number;
  readonly travel: number;
  readonly other: number;
}

export interface CategoryAverage {
  readonly total: number;
  readonly units: number;
  readonly average: number | null;
  readonly basis: AverageBasis | null;
  readonly items: number;
}

export interface PersonSummary {
  readonly person: LinePerson;
  readonly role: PersonRole;
  readonly events: number;
  /** Participants over their events that have a count. */
  readonly participants: number;
  readonly avgParticipants: number | null;
  readonly lastEventDate: string | null;
  readonly totals: SpendTotals;
  readonly avgFeePerEvent: number | null;
  readonly travelShare: number | null;
  readonly averages: Readonly<Record<SpendCategory, CategoryAverage>>;
  readonly flaggedCount: number;
  readonly flaggedAmount: number;
  readonly flaggedExcess: number;
}

export interface Norm {
  readonly category: SpendCategory;
  readonly scope: 'program' | 'category';
  readonly program: string | null;
  readonly median: number;
  readonly p90: number;
  readonly threshold: number;
  readonly sampleSize: number;
}

export interface Flag {
  readonly key: string;
  readonly expenseIds: readonly string[];
  readonly category: SpendCategory;
  readonly expenseItem: string;
  readonly description: string;
  readonly person: LinePerson;
  readonly opportunityId: string;
  readonly opportunityName: string;
  readonly accountName: string;
  readonly statementId: string;
  readonly eventDate: string | null;
  readonly program: string;
  readonly amount: number;
  readonly units: number;
  readonly basis: UnitBasis;
  readonly costPerUnit: number;
  readonly norm: Norm;
  readonly timesMedian: number | null;
  readonly excess: number;
  readonly receipt: { readonly submissionId: string; readonly receivedAt: string; readonly receiptUrl: string | null } | null;
}

export interface FacilitatorList {
  readonly window: SpendWindow;
  readonly role: RoleFilter;
  readonly summary: {
    readonly people: number;
    readonly totalPaid: number;
    readonly travelTotal: number;
    readonly travelShare: number | null;
    readonly flaggedCount: number;
    readonly flaggedAmount: number;
    readonly flaggedExcess: number;
  };
  readonly norms: Readonly<Record<SpendCategory, Norm | null>>;
  readonly people: readonly PersonSummary[];
}

export interface PersonEvent {
  readonly opportunityId: string;
  readonly opportunityName: string;
  readonly accountName: string;
  readonly statementId: string;
  readonly eventDate: string | null;
  readonly program: string;
  readonly participants: number | null;
  readonly eventCity: string;
  readonly eventState: string;
  readonly byCategory: Readonly<Record<SpendCategory, number>>;
  readonly total: number;
  readonly flaggedCount: number;
}

/** One person on one event: the export's row. */
export interface PersonEventExportRow extends PersonEvent {
  readonly person: LinePerson;
  readonly role: PersonRole;
}

export interface FacilitatorEvents {
  readonly window: SpendWindow;
  readonly role: RoleFilter;
  readonly rows: readonly PersonEventExportRow[];
}

export interface QuarterTrend {
  readonly quarter: string;
  readonly events: number;
  readonly totals: SpendTotals;
}

export interface FacilitatorDetail {
  readonly window: SpendWindow;
  readonly summary: PersonSummary;
  readonly norms: Readonly<Record<SpendCategory, Norm | null>>;
  readonly events: readonly PersonEvent[];
  readonly trend: readonly QuarterTrend[];
  readonly flags: readonly Flag[];
}

export interface FacilitatorOutliers {
  readonly window: SpendWindow;
  readonly totals: { readonly count: number; readonly amount: number; readonly excess: number };
  readonly programs: readonly string[];
  readonly flags: readonly Flag[];
}

// ─── Reads ───────────────────────────────────────────────────────────────────

export interface WindowParams {
  readonly from: string;
  /** '' for through today. */
  readonly to: string;
}

function windowQuery(window: WindowParams): Record<string, string> {
  return { from: window.from, ...(window.to ? { to: window.to } : {}) };
}

export const FACILITATOR_QUERY_KEY = {
  list: (window: WindowParams, role: RoleFilter) => ['facilitator-spend', 'list', window.from, window.to, role] as const,
  outliers: (window: WindowParams, category: string, program: string) =>
    ['facilitator-spend', 'outliers', window.from, window.to, category, program] as const,
  person: (window: WindowParams, contactId: string) => ['facilitator-spend', 'person', window.from, window.to, contactId] as const,
};

/** The reads join every expense line since 2023: keep a result for a few minutes. */
const SPEND_STALE_MS = 5 * 60_000;

export function useFacilitatorList(window: WindowParams, role: RoleFilter) {
  return useQuery({
    queryKey: FACILITATOR_QUERY_KEY.list(window, role),
    queryFn: ({ signal }) => getJson<FacilitatorList>('/accounting/facilitators', { ...windowQuery(window), role }, signal),
    staleTime: SPEND_STALE_MS,
  });
}

export function useFacilitatorOutliers(window: WindowParams, filter: { category: string; program: string }, enabled = true) {
  return useQuery({
    queryKey: FACILITATOR_QUERY_KEY.outliers(window, filter.category, filter.program),
    queryFn: ({ signal }) =>
      getJson<FacilitatorOutliers>(
        '/accounting/facilitators/outliers',
        {
          ...windowQuery(window),
          ...(filter.category ? { category: filter.category } : {}),
          ...(filter.program ? { program: filter.program } : {}),
        },
        signal,
      ),
    staleTime: SPEND_STALE_MS,
    enabled,
  });
}

export function useFacilitatorDetail(window: WindowParams, contactId: string) {
  return useQuery({
    queryKey: FACILITATOR_QUERY_KEY.person(window, contactId),
    queryFn: ({ signal }) =>
      getJson<FacilitatorDetail>(`/accounting/facilitators/${encodeURIComponent(contactId)}`, windowQuery(window), signal),
    staleTime: SPEND_STALE_MS,
  });
}

/** Every person's events in the window, fetched on demand for the export. */
export function fetchFacilitatorEvents(window: WindowParams, role: RoleFilter): Promise<FacilitatorEvents> {
  return getJson<FacilitatorEvents>('/accounting/facilitators/events', { ...windowQuery(window), role });
}

// ─── URL state ───────────────────────────────────────────────────────────────

/** Every filter lives in the URL, so a view can be shared and survives Back. */
export const SPEND_PARAM = {
  from: 'from',
  to: 'to',
  role: 'role',
  view: 'view',
  category: 'category',
  program: 'program',
} as const;
type SpendParam = (typeof SPEND_PARAM)[keyof typeof SPEND_PARAM];

export function parseRoleFilter(value: string | null): RoleFilter {
  return value === ROLE_FILTER.facilitator || value === ROLE_FILTER.assistant ? value : ROLE_FILTER.all;
}

export function parseSpendCategory(value: string | null): SpendCategory | '' {
  return SPEND_CATEGORY_ORDER.find((category) => category === value) ?? '';
}

/**
 * The window (`?from=&to=`, defaulting to 2023 through today) and a setter
 * for any filter param. `windowSearch` is the window alone, for links to a
 * person's page.
 */
export function useSpendParams() {
  const [params, setParams] = useSearchParams();
  const window: WindowParams = {
    from: params.get(SPEND_PARAM.from) || SPEND_DEFAULT_FROM,
    to: params.get(SPEND_PARAM.to) ?? '',
  };
  const windowParams = new URLSearchParams();
  if (params.get(SPEND_PARAM.from)) windowParams.set(SPEND_PARAM.from, window.from);
  if (window.to) windowParams.set(SPEND_PARAM.to, window.to);
  const windowSearch = windowParams.toString() ? `?${windowParams.toString()}` : '';

  /** Sets or clears params in one URL write (replace, so filters do not stack history). */
  function setParam(updates: Partial<Record<SpendParam | 'sort' | 'dir', string>>): void {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(updates)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  }

  return { params, window, windowSearch, setParam };
}

// ─── Display helpers ─────────────────────────────────────────────────────────

export { salesforceStatementUrl } from './salesforce';

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const moneyCents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2, minimumFractionDigits: 2 });

export function spendMoney(value: number): string {
  return money.format(value);
}

/** Small per-unit figures keep their cents ($58.78); larger ones round. */
export function unitMoney(value: number): string {
  return Math.abs(value) < 100 ? moneyCents.format(value) : money.format(value);
}

export function spendShare(value: number | null): string {
  return value === null ? '—' : `${Math.round(value * 100)}%`;
}

/** "3.4×" the median, one decimal. */
export function timesMedianLabel(value: number | null): string {
  return value === null ? '—' : `${value.toLocaleString('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 1 })}×`;
}

/** An average with its unit: "$187 per stay", or "—" when nothing was billed. */
export function averageLabel(average: CategoryAverage): string {
  if (average.average === null) return '—';
  return `${unitMoney(average.average)} ${average.basis ? BASIS_LABELS[average.basis] : ''}`.trim();
}

/** "Oct 5, 2026" from yyyy-mm-dd. */
export function eventDateLabel(value: string | null): string {
  if (!value) return '—';
  return new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// ─── Flattened rows (sortRows / filterRows read top-level fields) ────────────

/** Travel totals sit at the top level under their category key, so sortRows can read them. */
export interface PersonRow extends Readonly<Record<TravelCategory, number>> {
  readonly contactId: string;
  readonly name: string;
  readonly email: string;
  readonly userId: string | null;
  readonly role: PersonRole;
  readonly roleLabel: string;
  readonly events: number;
  readonly participants: number;
  readonly avgParticipants: number | null;
  readonly lastEventDate: string | null;
  readonly total: number;
  readonly avgFeePerEvent: number | null;
  readonly travel: number;
  readonly travelShare: number | null;
  readonly airfareAvg: number | null;
  readonly hotelAvg: number | null;
  readonly mealsAvg: number | null;
  readonly mileageAvg: number | null;
  readonly parkingAvg: number | null;
  readonly miscTravelAvg: number | null;
  readonly flaggedCount: number;
  readonly flaggedAmount: number;
  readonly summary: PersonSummary;
}

export function toPersonRows(people: readonly PersonSummary[]): PersonRow[] {
  return people.map((summary) => ({
    contactId: summary.person.contactId,
    name: summary.person.name || summary.person.email || 'Unnamed contact',
    email: summary.person.email,
    userId: summary.person.userId,
    role: summary.role,
    roleLabel: PERSON_ROLE_LABELS[summary.role],
    events: summary.events,
    participants: summary.participants,
    avgParticipants: summary.avgParticipants,
    lastEventDate: summary.lastEventDate,
    ...perTravelCategory((category) => summary.averages[category].total),
    total: summary.totals.total,
    avgFeePerEvent: summary.avgFeePerEvent,
    travel: summary.totals.travel,
    travelShare: summary.travelShare,
    airfareAvg: summary.averages.airfare.average,
    hotelAvg: summary.averages.hotel.average,
    mealsAvg: summary.averages.meals.average,
    mileageAvg: summary.averages.mileage.average,
    parkingAvg: summary.averages.parking.average,
    miscTravelAvg: summary.averages.miscTravel.average,
    flaggedCount: summary.flaggedCount,
    flaggedAmount: summary.flaggedAmount,
    summary,
  }));
}

export interface FlagRow {
  readonly key: string;
  readonly personName: string;
  readonly contactId: string;
  readonly categoryLabel: string;
  readonly opportunityName: string;
  readonly accountName: string;
  readonly program: string;
  readonly description: string;
  readonly eventDate: string | null;
  readonly amount: number;
  readonly costPerUnit: number;
  readonly median: number;
  readonly timesMedian: number | null;
  readonly excess: number;
  readonly flag: Flag;
}

export function toFlagRows(flags: readonly Flag[]): FlagRow[] {
  return flags.map((flag) => ({
    key: flag.key,
    personName: flag.person.name || flag.person.email || 'Unnamed contact',
    contactId: flag.person.contactId,
    categoryLabel: SPEND_CATEGORY_LABELS[flag.category],
    opportunityName: flag.opportunityName,
    accountName: flag.accountName,
    program: flag.program,
    description: flag.description,
    eventDate: flag.eventDate,
    amount: flag.amount,
    costPerUnit: flag.costPerUnit,
    median: flag.norm.median,
    timesMedian: flag.timesMedian,
    excess: flag.excess,
    flag,
  }));
}

export interface PersonEventRow extends Readonly<Record<TravelCategory, number>> {
  readonly statementId: string;
  readonly opportunityId: string;
  readonly opportunityName: string;
  readonly accountName: string;
  readonly program: string;
  readonly eventDate: string | null;
  readonly participants: number | null;
  readonly fee: number;
  readonly assistant: number;
  readonly travel: number;
  readonly other: number;
  readonly total: number;
  readonly flaggedCount: number;
  readonly event: PersonEvent;
}

export function toPersonEventRows(events: readonly PersonEvent[]): PersonEventRow[] {
  return events.map((event) => ({
    statementId: event.statementId,
    opportunityId: event.opportunityId,
    opportunityName: event.opportunityName,
    accountName: event.accountName,
    program: event.program,
    eventDate: event.eventDate,
    participants: event.participants,
    fee: event.byCategory.fee,
    ...perTravelCategory((category) => event.byCategory[category]),
    assistant: event.byCategory.assistant,
    travel: TRAVEL_CATEGORIES.reduce((sum, category) => sum + event.byCategory[category], 0),
    other: event.byCategory.other,
    total: event.total,
    flaggedCount: event.flaggedCount,
    event,
  }));
}

// ─── CSV export ──────────────────────────────────────────────────────────────

/** RFC 4180 quoting, and a leading quote on text a spreadsheet would run as a formula. */
function csvCell(value: string | number | null): string {
  if (value === null) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(Math.round(value * 100) / 100) : '';
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** The export's columns: who, the event, then every category broken out. */
const EXPORT_COLUMNS: ReadonlyArray<{ readonly header: string; readonly value: (row: PersonEventExportRow) => string | number | null }> = [
  { header: 'Person', value: (row) => row.person.name },
  { header: 'Email', value: (row) => row.person.email },
  { header: 'Role', value: (row) => PERSON_ROLE_LABELS[row.role] },
  { header: 'Event date', value: (row) => row.eventDate },
  { header: 'Event', value: (row) => row.opportunityName },
  { header: 'Client', value: (row) => row.accountName },
  { header: 'Program', value: (row) => row.program },
  { header: 'City', value: (row) => row.eventCity },
  { header: 'State', value: (row) => row.eventState },
  { header: 'Participants', value: (row) => row.participants },
  ...SPEND_CATEGORY_ORDER.map((category) => ({
    header: SPEND_CATEGORY_LABELS[category],
    value: (row: PersonEventExportRow) => row.byCategory[category],
  })),
  { header: 'Travel total', value: (row) => TRAVEL_CATEGORIES.reduce((sum, category) => sum + row.byCategory[category], 0) },
  { header: 'Total', value: (row) => row.total },
  { header: 'Lines worth a look', value: (row) => row.flaggedCount },
  { header: 'Opportunity ID', value: (row) => row.opportunityId },
  { header: 'Statement ID', value: (row) => row.statementId },
];

/**
 * The export as CSV: one row per person per event, every spend category in
 * its own column, ready for a spreadsheet or to hand to Claude.
 */
export function facilitatorEventsCsv(rows: readonly PersonEventExportRow[]): string {
  const lines = [EXPORT_COLUMNS.map((column) => csvCell(column.header)).join(',')];
  for (const row of rows) lines.push(EXPORT_COLUMNS.map((column) => csvCell(column.value(row))).join(','));
  return `${lines.join('\r\n')}\r\n`;
}

/** "facilitator-costs-2023-01-01-to-2026-10-27.csv" */
export function facilitatorExportFileName(window: SpendWindow): string {
  return `facilitator-costs-${window.from}-to-${window.to}.csv`;
}
